import { NavLink, useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Bell, Search, UserCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthProvider';
import { apiFetch } from '../../lib/api';
import { useModal } from '../../context/ModalProvider';
import AccountMenuBubble from './AccountMenuBubble';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  cyberGlassBackdrop,
  cyberGlassTextMuted,
  cyberGlassTextPrimary,
} from '../../styles/cyberGlass';

/* ────────── animation keyframes ────────── */
const grow = keyframes`
  0%   { transform: scale(0.8); opacity: 0; }
  60%  { transform: scale(1.05); }
  100% { transform: scale(1);   opacity: 1; }
`;

const shrink = keyframes`
  0%   { transform: scale(1);   opacity: 1; }
  40%  { transform: scale(1.05);}
  100% { transform: scale(0.8); opacity: 0; }
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-100%); }
`;


/* ────────── backend panel palette ────────── */
const TEXT_PRIMARY = cyberGlassTextPrimary;
const TEXT_MUTED = cyberGlassTextMuted;
const TILE_BG = 'rgba(10, 14, 32, 0.4)';
const TILE_BG_HOVER = 'rgba(17, 23, 50, 0.56)';
const TILE_ACTIVE_BORDER = 'none';
const TILE_ACTIVE_BG = 'transparent';
const TILE_ACTIVE_TEXT = '#E0A15E';

/* ────────── styled components ────────── */
interface ContainerProps { 
  $collapsed: boolean;
  $isMobile: boolean;
  $isOpen: boolean;
  $mobileWidth?: string;
}

const Container = styled.aside<ContainerProps>`
  color: ${TEXT_PRIMARY};
  background: linear-gradient(180deg, rgba(20,22,28,.96), rgba(18,20,25,.96));
  border: 1px solid rgba(255,255,255,.06);
  box-shadow: 0 18px 48px rgba(0,0,0,.45);

  /* Desktop styles */
  @media (min-width: 768px) {
    position: relative;
    width: ${({ $collapsed }) => ($collapsed ? '56px' : '220px')};
    transition: width 0.25s ease;
    height: calc(100vh - 2rem);
    border-radius: 16px;                         /* match AuthModal Card */
    animation: ${({ $collapsed }) => ($collapsed ? shrink : grow)} 0.35s
      cubic-bezier(.22,1.44,.46,.99);
  }

  /* Mobile styles */
  @media (max-width: 767px) {
    position: fixed;
    top: 0;
    left: 0;
    width: ${({ $mobileWidth }) => $mobileWidth ?? '280px'};
    max-width: 100vw;
    height: 100vh;
    box-shadow: 2px 0 24px rgba(0,0,0,.45);
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(-100%)')};
    animation: ${({ $isOpen }) => ($isOpen ? slideIn : slideOut)} 0.3s ease-in-out;
    animation-fill-mode: forwards;
    z-index: 1000;
    border-radius: 0 16px 16px 0;
  }

  /* Common styles */
  overflow: hidden;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: 767px) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    ${cyberGlassBackdrop};
    z-index: 999;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  
  @media (max-width: 767px) {
    display: flex;
    position: fixed;
    top: 1rem;
    left: 1rem;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(216, 216, 216, 0.5);
    background: rgba(35, 35, 36, 0.7);
    color: ${TEXT_MUTED};
    cursor: pointer;
    z-index: 998;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 4px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(20, 33, 72, .72);
    }

    span {
      width: 24px;
      height: 2px;
      background: currentColor;
      transition: all 0.3s ease;
    }

    &.open {
      span:nth-child(1) {
        transform: rotate(45deg) translate(5px, 5px);
      }
      span:nth-child(2) {
        opacity: 0;
      }
      span:nth-child(3) {
        transform: rotate(-45deg) translate(5px, -5px);
      }
    }
  }
`;

const StyledLink = styled(NavLink).attrs({ end: true })<{
  $collapsed?: boolean;
  $nested?: boolean;
  $level?: number;
}>`
  position: relative;
  text-decoration: none;
  color: ${TEXT_MUTED};
  padding: ${({ $nested, $level = 0 }) =>
    $nested
      ? `0.55rem 0.9rem 0.55rem ${1.2 + $level * 0.85}rem`
      : '0.65rem 0.9rem'};
  border-radius: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  // border: 1px solid rgba(141, 235, 255, .26);
  // background: ${TILE_BG};
  transition: background .2s ease, border-color .2s ease, color .2s ease, transform .06s ease;

  &:hover { 
  border: 2px solid #666;
  // background: ${TILE_BG_HOVER}; 
  }

  &.active {
    border: ${TILE_ACTIVE_BORDER};
    background: ${TILE_ACTIVE_BG};
    color: ${TILE_ACTIVE_TEXT};
  }

  &.active:hover {
    border: none;
    background: transparent;
    color: ${TILE_ACTIVE_TEXT};
  }

  @media (min-width: 768px) {
    justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  }
`;

const NavGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const GroupToggle = styled.button<{
  $collapsed?: boolean;
  $active?: boolean;
}>`
  all: unset;
  position: relative;
  color: ${({ $active }) => ($active ? TILE_ACTIVE_TEXT : TEXT_MUTED)};
  padding: 0.65rem 0.9rem;
  border-radius: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  cursor: pointer;
  transition: background .2s ease, border-color .2s ease, color .2s ease, transform .06s ease;

  &:hover {
    border: 2px solid #666;
  }

  @media (min-width: 768px) {
    justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'space-between')};
  }
`;

const GroupLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
`;

const IconGlyph = styled.span`
  display: inline-grid;
  place-items: center;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
`;

const GroupChevron = styled.span<{ $open: boolean; $hidden?: boolean }>`
  font-size: 1rem;
  font-weight: 900;
  line-height: 1;
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.2s ease;
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
`;

const GroupChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const CloseButton = styled.button`
  display: none;

  @media (max-width: 767px) {
    display: grid;
    place-items: center;
    position: absolute;
    top: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid rgba(102, 102, 102, 0.2);
    background: rgba(41, 41, 41, 0.54);
    color: ${TEXT_MUTED};
    font-size: 18px;
    cursor: pointer;
    transition: background .2s ease;

    &:hover {
      background: rgba(48, 48, 49, 0.3);
    }
  }
`;

const MobileHeader = styled.div`
  display: none;
  
  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(141, 235, 255, .24);
  }
`;

const Logo = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: ${TEXT_PRIMARY};
  font-weight: 800;
`;



const BellWrap = styled.button`
  all: unset;
  position: relative;
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  color: ${TEXT_MUTED};
  cursor: pointer;
  overflow: visible;
  transition: color .15s ease, transform .12s ease;

  &:hover {
    color: ${TEXT_PRIMARY};
    transform: translateY(-1px);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #a64c44;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  display: grid;
  place-items: center;
  border: 1px solid rgba(0,0,0,.25);
  line-height: 1;
`;

const TopBar = styled.div`
  display: grid;
  grid-auto-flow: column;
  justify-content: start;
  align-items: center;
  gap: 10px;
  margin-bottom: .5rem;
`;

const FlatIconBtn = styled.button`
  all: unset;
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  color: ${TEXT_MUTED};
  cursor: pointer;
  transition: color .15s ease, transform .12s ease;

  &:hover {
    color: ${TEXT_PRIMARY};
    transform: translateY(-1px);
  }
`;

/* footer */
const Footer = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 0.5px solid #969696;
  display: flex;
  flex-direction: column;
  gap: .5rem;
  font-size: .85rem;
`;





/* Pill gradient CTA that mirrors AuthModal PrimaryCta (as a NavLink) */
const PrimaryCtaLink = styled(NavLink)`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 44px;
  padding: 12px 16px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,.25);
  background: linear-gradient(90deg, #8DEBFF 0%, #F8C9A5 100%);
  color: #111827;
  font-weight: 800;
  letter-spacing: .3px;
  text-transform: uppercase;
  text-decoration: none;
  box-shadow:
    0 2px 6px rgba(0,0,0,.18),
    0 8px 24px rgba(141,235,255,.25);
  transition: transform .06s ease, box-shadow .15s ease, filter .2s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(255,255,255,.25), rgba(255,255,255,0) 60%);
    pointer-events: none;
  }
  &:hover {
    box-shadow:
      0 3px 10px rgba(0,0,0,.22),
      0 10px 32px rgba(141,235,255,.30);
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
    box-shadow:
      0 2px 6px rgba(0,0,0,.22),
      0 6px 20px rgba(141,235,255,.25);
  }
  &:focus-visible { outline: 2px solid #8ab4f8; outline-offset: 2px; }
`;

/* ────────── public types ────────── */
export interface LinkDef {
  to?: string;
  label: string;
  icon?: React.ReactNode;
  children?: LinkDef[];
  key?: string;
}

/* ────────── component ────────── */
interface Props {
  links: LinkDef[];
  collapsed: boolean;
  mobileOpen?: boolean;
  onMobileToggle?: () => void;
  createTicketLink?: LinkDef & { to: string };
  mobileWidth?: string;
}

export default function FloatingSidebar({ 
  links, 
  collapsed, 
  mobileOpen = false,
  onMobileToggle,
  createTicketLink,
  mobileWidth,
}: Props) {
  const { role } = useAuth() as any;
  const location = useLocation();
  const modal = useModal();
  const [isMobile, setIsMobile] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);



  // Lightweight poll for unread; falls back to scanning latest 25
  const loadUnread = useCallback(async () => {
    try {
      const data = await apiFetch<any>('/api/notifications?limit=25');
      const arr = Array.isArray(data) ? data : (data.notifications ?? []);
      const total = typeof data?.totalUnread === 'number'
        ? data.totalUnread
        : arr.filter((n: any) => n && n.read === false).length;
      setUnreadCount(total);
    } catch {
      // ignore network errors; badge will stale until next poll
    }
  }, []);

  useEffect(() => {
    loadUnread();
    const id = window.setInterval(loadUnread, 15000);
    const onUpdated = () => loadUnread();
    window.addEventListener('notifications:updated', onUpdated);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('notifications:updated', onUpdated);
    };
  }, [loadUnread]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (typeof event.key !== 'string') return;
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tagName = target.tagName.toLowerCase();
        if (
          target.isContentEditable ||
          tagName === 'input' ||
          tagName === 'textarea' ||
          tagName === 'select'
        ) {
          return;
        }
      }
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && key === 'k') {
        event.preventDefault();
        modal.open('global-search');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modal]);


  const handleLinkClick = () => {
    if (isMobile && onMobileToggle) {
      onMobileToggle();
    }
  };

  const isLinkGroup = (link: LinkDef): boolean =>
    Array.isArray(link.children) && link.children.length > 0;

  const getLinkKey = (link: LinkDef, index: number, parentKey = 'root'): string =>
    link.key ?? link.to ?? `${parentKey}-${link.label}-${index}`;

  const hasTopLevelProfileLink = links.some(
    (link) =>
      (link.to ?? '').toLowerCase() === '/dashboard/profile' ||
      link.label.trim().toLowerCase() === 'profile',
  );
  const profileLink: LinkDef = {
    to: '/dashboard/profile',
    label: 'Profile',
    icon: <UserCircle2 size={16} />,
    key: '__sidebar_profile_link__',
  };
  const overviewIndex = links.findIndex(
    (link) => link.label.trim().toLowerCase() === 'overview',
  );
  const linksWithProfile = hasTopLevelProfileLink
    ? links
    : overviewIndex >= 0
      ? [
          ...links.slice(0, overviewIndex + 1),
          profileLink,
          ...links.slice(overviewIndex + 1),
        ]
      : [profileLink, ...links];

  const normalizeLabel = (value: string | undefined): string => (value ?? '').trim().toLowerCase();

  // Ensure "Global Queue" is nested under Teams.
  const hasTeamsDirectory = linksWithProfile.some(
    (link) => isLinkGroup(link) && normalizeLabel(link.label) === 'teams',
  );
  const globalQueueTopLevel = hasTeamsDirectory
    ? linksWithProfile.find(
        (link) => !isLinkGroup(link) && normalizeLabel(link.label) === 'global queue',
      )
    : undefined;
  const linksWithoutGlobalQueue = globalQueueTopLevel
    ? linksWithProfile.filter(
        (link) =>
          !(
            !isLinkGroup(link) &&
            normalizeLabel(link.label) === 'global queue' &&
            (link.to ?? '') === (globalQueueTopLevel.to ?? '')
          ),
      )
    : linksWithProfile;

  const linksFinal = linksWithoutGlobalQueue.map((link) => {
    if (!isLinkGroup(link) || normalizeLabel(link.label) !== 'teams' || !globalQueueTopLevel) {
      return link;
    }

    const children = Array.isArray(link.children) ? link.children : [];
    const hasGlobalQueueChild = children.some(
      (child) =>
        normalizeLabel(child.label) === 'global queue' ||
        ((child.to ?? '').trim().toLowerCase() === (globalQueueTopLevel.to ?? '').trim().toLowerCase() &&
          Boolean(child.to)),
    );

    if (hasGlobalQueueChild) return link;
    return { ...link, children: [...children, globalQueueTopLevel] };
  });

  const hasActiveChild = (link: LinkDef): boolean => {
    if (!link.children?.length) return false;
    return link.children.some((child) => {
      if (child.to && location.pathname.startsWith(child.to)) return true;
      if (child.children?.length) return hasActiveChild(child);
      return false;
    });
  };

  const toggleGroup = (key: string, depth: number) => {
    setOpenGroups((prev) => {
      if (depth === 0) {
        const isOpen = Boolean(prev[key]);
        // Exactly one top-level directory can be open at once.
        return isOpen ? {} : { [key]: true };
      }
      return {
        ...prev,
        [key]: !(prev[key] ?? false),
      };
    });
  };

  const renderLink = (
    link: LinkDef,
    index: number,
    parentKey = 'root',
    depth = 0,
  ): ReactNode => {
    const sidebarIsCollapsed = collapsed && !isMobile;
    const linkKey = getLinkKey(link, index, parentKey);

    if (isLinkGroup(link)) {
      const active = hasActiveChild(link);
      const open =
        depth === 0
          ? Object.keys(openGroups).length > 0
            ? Boolean(openGroups[linkKey])
            : active
          : Boolean(openGroups[linkKey]);

      return (
        <NavGroup key={linkKey}>
          <GroupToggle
            type="button"
            title={link.label}
            $collapsed={sidebarIsCollapsed}
            $active={active}
            onClick={() => toggleGroup(linkKey, depth)}
            style={!sidebarIsCollapsed && depth > 0 ? { paddingLeft: `${0.9 + depth * 0.85}rem` } : undefined}
          >
            <GroupLabel>
              {link.icon ? <IconGlyph>{link.icon}</IconGlyph> : null}
              {sidebarIsCollapsed ? (link.icon ? null : link.label.charAt(0)) : link.label}
            </GroupLabel>
            <GroupChevron $open={open} $hidden={sidebarIsCollapsed}>
              {String.fromCharCode(9662)}
            </GroupChevron>
          </GroupToggle>

          {!sidebarIsCollapsed && open ? (
            <GroupChildren>
              {link.children?.map((child, childIndex) =>
                renderLink(child, childIndex, linkKey, depth + 1),
              )}
            </GroupChildren>
          ) : null}
        </NavGroup>
      );
    }

    if (!link.to) return null;
    return (
      <StyledLink
        key={linkKey}
        to={link.to}
        title={link.label}
        $collapsed={sidebarIsCollapsed}
        $nested={depth > 0}
        $level={depth}
        onClick={handleLinkClick}
      >
        {link.icon ? <IconGlyph>{link.icon}</IconGlyph> : null}
        {sidebarIsCollapsed ? (link.icon ? null : link.label.charAt(0)) : link.label}
      </StyledLink>
    );
  };

  const showSearch = role !== 'client';
  const showNotifications = role === 'agent' || role === 'admin' || role === 'client';

  return (
    <>
      <HamburgerButton 
        onClick={onMobileToggle}
        className={mobileOpen ? 'open' : ''}
        aria-label="Toggle menu"
      >
        <span />
        <span />
        <span />
      </HamburgerButton>

      <Overlay $isOpen={mobileOpen} onClick={onMobileToggle} />
      
      <Container 
        $collapsed={collapsed} 
        $isMobile={isMobile}
        $isOpen={mobileOpen}
        $mobileWidth={mobileWidth}
      >
        <MobileHeader>
          <Logo>CYBARD</Logo>
          <CloseButton onClick={onMobileToggle} aria-label="Close menu">
            ×
          </CloseButton>
        </MobileHeader>
        {showSearch || showNotifications ? (
          <TopBar>
            {showSearch ? (
              <FlatIconBtn
                onClick={() => {
                  modal.open('global-search');
                  if (isMobile && onMobileToggle) onMobileToggle();
                }}
                aria-label="Open global search"
                title="Search (Ctrl/Cmd + K)"
              >
                <Search size={18} />
              </FlatIconBtn>
            ) : null}
            {showNotifications ? (
              <BellWrap
                onClick={() => {
                  modal.open('notifications');
                  if (isMobile && onMobileToggle) onMobileToggle();
                }}
                aria-label={`Open notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
                title="Notifications"
                type="button"
              >
                  <Bell size={18} />
                {unreadCount > 0 && (
                  <Badge>{unreadCount > 99 ? '99' : unreadCount}</Badge>
                )}
              </BellWrap>
            ) : null}
          </TopBar>
        ) : null}


        {linksFinal.map((link, index) => renderLink(link, index))}

        <Footer>
          {createTicketLink && (
            <PrimaryCtaLink
              to={createTicketLink.to}
              title={createTicketLink.label}
              onClick={handleLinkClick}
              style={{ marginBottom: '1rem' }}
            >
              {createTicketLink.icon ? <IconGlyph>{createTicketLink.icon}</IconGlyph> : null}
             {(collapsed && !isMobile) ? (createTicketLink.icon ? null : '+') : createTicketLink.label}
            </PrimaryCtaLink>
          )}
          <AccountMenuBubble collapsed={collapsed && !isMobile} />
        </Footer>
      </Container>
    </>
  );
}




